import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 64
const CHANNEL_SIZE = 32
const POWER_MASK = 0xc0
const NARROW_MASK = 0x02
const SCAN_MASK = 0x20
const AIRBAND = [118_000_000, 136_000_000] as const

type JC8810Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  magic: string
  fingerprints: readonly (readonly number[])[]
  memorySize: number
  ranges: readonly (readonly [number, number])[]
  channels: 256 | 512
  powerLevels: readonly string[]
  airband?: boolean
}

export type JC8810Profile = {
  protocol: 'jc8810'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 57600
  channelMaximum: number
  nameLength: 12
  memorySize: number
  ranges: readonly (readonly [number, number])[]
  magic: readonly number[]
  fingerprints: readonly (readonly number[])[]
  powerLevels: readonly string[]
  airband: boolean
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

function bytes(...values: number[]): readonly number[] {
  return values
}

const DEFAULT_RANGES = [
  [0x0000, 0x2000],
  [0x8000, 0x8040],
  [0x9000, 0x9040],
  [0xa000, 0xa140],
  [0xb000, 0xb300],
] as const
const EXTENDED_RANGES = [
  [0x0000, 0x2000],
  [0x8000, 0x8040],
  [0x9000, 0x9040],
  [0xa000, 0xa140],
  [0xb000, 0xb440],
] as const
const TALKPLUS_RANGES = [
  [0x0000, 0x4000],
  [0x8000, 0x8040],
  [0x9000, 0x9040],
  [0xa000, 0xa140],
  [0xb000, 0xb440],
] as const
const RT630_RANGES = [
  [0x0000, 0x2000],
  [0x8000, 0x8040],
  [0x9000, 0x9040],
  [0xa000, 0xa140],
  [0xb000, 0xb2c0],
  [0xb500, 0xb740],
] as const

const SPECS: readonly JC8810Spec[] = [
  {
    id: 'radtel-rt-470',
    vendor: 'Radtel',
    model: 'RT-470',
    summary: 'Radtel RT-470 256-channel handheld.',
    magic: 'PROGRAMJC81U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0x26, 0x00, 0x20, 0xd8, 0x04),
      bytes(0x00, 0x00, 0x00, 0x42, 0x00, 0x20, 0xf0, 0x04),
      bytes(0x00, 0x00, 0x00, 0x4a, 0x00, 0x20, 0xf8, 0x04),
      bytes(0x00, 0x00, 0x00, 0x3a, 0x00, 0x20, 0xe8, 0x04),
      bytes(0x00, 0x00, 0x00, 0x42, 0x00, 0x20, 0xec, 0x04),
      bytes(0x00, 0x00, 0x00, 0x28, 0x00, 0x20, 0xd4, 0x04),
      bytes(0x00, 0x00, 0x00, 0x2c, 0x00, 0x20, 0xd8, 0x04),
      bytes(0x00, 0x00, 0x00, 0x36, 0x00, 0x20, 0xdc, 0x04),
    ],
    memorySize: 0xb300,
    ranges: DEFAULT_RANGES,
    channels: 256,
    powerLevels: ['High', 'Mid', 'Low'],
  },
  {
    id: 'radtel-rt-470l',
    vendor: 'Radtel',
    model: 'RT-470L',
    summary: 'Radtel RT-470L 256-channel handheld.',
    magic: 'PROGRAMJC81U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0xfe, 0x00, 0x20, 0xac, 0x04),
      bytes(0x00, 0x00, 0x00, 0x20, 0x00, 0x20, 0xcc, 0x04),
      bytes(0x00, 0x00, 0x00, 0x20, 0x00, 0x20, 0x07, 0x00),
    ],
    memorySize: 0xb300,
    ranges: DEFAULT_RANGES,
    channels: 256,
    powerLevels: ['High', 'Mid', 'Low'],
  },
  {
    id: 'radtel-rt-470x',
    vendor: 'Radtel',
    model: 'RT-470X',
    summary: 'Radtel RT-470X wideband handheld with AM airband.',
    magic: 'PROGRAMJC81U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0x20, 0x00, 0x20, 0xcc, 0x04),
      bytes(0x00, 0x00, 0x00, 0x2c, 0x00, 0x20, 0xd8, 0x04),
      bytes(0x00, 0x00, 0x00, 0x36, 0x00, 0x20, 0xdc, 0x04),
    ],
    memorySize: 0xb300,
    ranges: DEFAULT_RANGES,
    channels: 256,
    powerLevels: ['High', 'Mid', 'Low'],
    airband: true,
  },
  {
    id: 'radtel-rt-470x-bt',
    vendor: 'Radtel',
    model: 'RT-470X_BT',
    summary: 'Radtel RT-470X Plus BT wideband handheld.',
    magic: 'PROGRAMJC81U',
    fingerprints: [bytes(0x01, 0x36, 0x01, 0x80, 0x04, 0x00, 0x05, 0x20)],
    memorySize: 0xb300,
    ranges: DEFAULT_RANGES,
    channels: 256,
    powerLevels: ['High', 'Mid', 'Low'],
    airband: true,
  },
  {
    id: 'hiroyasu-hi-8811',
    vendor: 'Hiroyasu',
    model: 'HI-8811',
    summary: 'Hiroyasu HI-8811 256-channel handheld.',
    magic: 'PROGRAMJC81U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0xfe, 0x00, 0x20, 0xac, 0x04),
      bytes(0x00, 0x00, 0x00, 0x20, 0x00, 0x20, 0xcc, 0x04),
      bytes(0x00, 0x00, 0x00, 0x20, 0x00, 0x20, 0x07, 0x00),
      bytes(0x00, 0x00, 0x00, 0x28, 0x00, 0x20, 0xd4, 0x04),
      bytes(0x00, 0x00, 0x00, 0x28, 0x00, 0x20, 0x07, 0x00),
    ],
    memorySize: 0xb300,
    ranges: DEFAULT_RANGES,
    channels: 256,
    powerLevels: ['High', 'Mid', 'Low'],
  },
  {
    id: 'anysecu-uv-a37',
    vendor: 'Anysecu',
    model: 'UV-A37',
    summary: 'Anysecu UV-A37 256-channel handheld.',
    magic: 'PROGRAMJC37U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0xe4, 0x00, 0x20, 0x94, 0x04),
      bytes(0x00, 0x00, 0x00, 0xe8, 0x00, 0x20, 0x98, 0x04),
    ],
    memorySize: 0xb440,
    ranges: EXTENDED_RANGES,
    channels: 256,
    powerLevels: ['High', 'Low'],
  },
  {
    id: 'talkpod-a36plus',
    vendor: 'Talkpod',
    model: 'A36plus',
    summary: 'Talkpod A36plus 512-channel handheld.',
    magic: 'PROGRAMJC37U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0x42, 0x00, 0x20, 0xf0, 0x04),
      bytes(0x00, 0x00, 0x00, 0x5a, 0x00, 0x20, 0x08, 0x05),
      bytes(0x00, 0x00, 0x00, 0x9e, 0x00, 0x20, 0x0c, 0x05),
      bytes(0x00, 0x00, 0x00, 0xfa, 0x00, 0x20, 0x40, 0x05),
      bytes(0x00, 0x00, 0x00, 0x9c, 0x00, 0x20, 0x04, 0x05),
    ],
    memorySize: 0xb440,
    ranges: TALKPLUS_RANGES,
    channels: 512,
    powerLevels: ['High', 'Low'],
  },
  {
    id: 'talkpod-a36plus-8w',
    vendor: 'Talkpod',
    model: 'A36plus_8w',
    summary: 'Talkpod A36plus 8W 512-channel handheld.',
    magic: 'PROGRAMJC37U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0xfa, 0x00, 0x20, 0x40, 0x05),
      bytes(0x00, 0x00, 0x00, 0xd8, 0x00, 0x20, 0x58, 0x05),
    ],
    memorySize: 0xb440,
    ranges: TALKPLUS_RANGES,
    channels: 512,
    powerLevels: ['High', 'Low'],
  },
  {
    id: 'abbree-ar-730',
    vendor: 'Abbree',
    model: 'AR-730',
    summary: 'Abbree AR-730 256-channel handheld.',
    magic: 'PROGRAMJC37U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0xe4, 0x00, 0x20, 0x94, 0x04),
      bytes(0x00, 0x00, 0x00, 0xe8, 0x00, 0x20, 0x98, 0x04),
    ],
    memorySize: 0xb440,
    ranges: EXTENDED_RANGES,
    channels: 256,
    powerLevels: ['High', 'Low'],
  },
  {
    id: 'radtel-rt-630',
    vendor: 'Radtel',
    model: 'RT-630',
    summary: 'Radtel RT-630 wideband 256-channel handheld.',
    magic: 'PROGRAMJC81U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0x32, 0x00, 0x20, 0xd8, 0x04),
      bytes(0x00, 0x00, 0x00, 0x36, 0x00, 0x20, 0xdc, 0x04),
    ],
    memorySize: 0xb740,
    ranges: RT630_RANGES,
    channels: 256,
    powerLevels: ['High', 'Mid', 'Low'],
  },
  {
    id: 'radtel-rt-495',
    vendor: 'Radtel',
    model: 'RT-495',
    summary: 'Radtel RT-495 wideband 256-channel handheld.',
    magic: 'PROGRAMJC81U',
    fingerprints: [
      bytes(0x00, 0x00, 0x00, 0x24, 0x00, 0x20, 0xd0, 0x04),
      bytes(0x00, 0x00, 0x00, 0x32, 0x00, 0x20, 0xd8, 0x04),
    ],
    memorySize: 0xb740,
    ranges: RT630_RANGES,
    channels: 256,
    powerLevels: ['High', 'Mid', 'Low'],
  },
]

export const JC8810_PROFILES: readonly JC8810Profile[] = SPECS.map((spec) => ({
  protocol: 'jc8810',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: [
    'Experimental',
    'Read + write',
    `${spec.channels} channels`,
    '12-character names',
  ],
  baudRate: 57600,
  channelMaximum: spec.channels,
  nameLength: 12,
  memorySize: spec.memorySize,
  ranges: spec.ranges,
  magic: ascii(spec.magic),
  fingerprints: spec.fingerprints,
  powerLevels: spec.powerLevels,
  airband: spec.airband ?? false,
}))

function emptyChannel(number: number, powerLevels: readonly string[]): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: powerLevels[0],
    skip: false,
  }
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(raw: Uint8Array): DecodedTone {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
  }
  if (value >= 0x0258) {
    return { mode: 'ctcss', ctcss: value / 10, dtcs: null, polarity: 'N' }
  }
  const reversed = value >= 0x6a
  const index = (reversed ? value - 0x6a : value - 1) & 0xffff
  return {
    mode: 'dtcs',
    ctcss: null,
    dtcs: DTCS_CODES[index] ?? null,
    polarity: reversed ? 'R' : 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(
      channel.dtcs as (typeof DTCS_CODES)[number],
    )
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    value = index + 1 + (channel.dtcsPolarity === 'R' ? 0x69 : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function powerFromCode(
  code: number,
  profile: JC8810Profile,
): string {
  if (profile.powerLevels.length === 2) {
    return code === 1 ? profile.powerLevels[1] : profile.powerLevels[0]
  }
  if (code === 1) return profile.powerLevels[2]
  if (code === 2) return profile.powerLevels[1]
  return profile.powerLevels[0]
}

function codeFromPower(
  power: string,
  profile: JC8810Profile,
): number {
  if (profile.powerLevels.length === 2) {
    return power === profile.powerLevels[1] ? 1 : 0
  }
  if (power === profile.powerLevels[2]) return 1
  if (power === profile.powerLevels[1]) return 2
  return 0
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff || value === 0 ? 0x20 : value)),
  ).trim()
}

function inAirband(frequency: number): boolean {
  return frequency >= AIRBAND[0] && frequency < AIRBAND[1]
}

export function parseJC8810Channel(
  image: Uint8Array,
  profile: JC8810Profile,
  number: number,
): WebChannel {
  const offset = (number - 1) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE || raw[0] === 0xff) {
    return emptyChannel(number, profile.powerLevels)
  }
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (!rxFrequency) return emptyChannel(number, profile.powerLevels)
  const txFrequency =
    raw[4] === 0xff ? null : decodeLbcd(raw.slice(4, 8))
  const txTone = decodeTone(raw.slice(10, 12))
  const rxtone = decodeTone(raw.slice(8, 10))
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const narrow = (raw[15] & NARROW_MASK) !== 0
  return {
    number,
    empty: false,
    name: decodeName(raw.slice(20, 32)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode:
      profile.airband && inAirband(rxFrequency) ? 'AM' : narrow ? 'NFM' : 'FM',
    power: powerFromCode((raw[14] & POWER_MASK) >> 6, profile),
    skip: (raw[15] & SCAN_MASK) === 0,
  }
}

export function parseJC8810Channels(
  image: Uint8Array,
  profile: JC8810Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseJC8810Channel(image, profile, index + 1),
  )
}

export function applyJC8810Channel(
  image: Uint8Array,
  profile: JC8810Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > profile.channelMaximum) {
    throw new Error(`Channel must be between 1 and ${profile.channelMaximum}.`)
  }
  const offset = (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const wasEmpty = raw[0] === 0xff
  if (wasEmpty) {
    raw.fill(0xff)
    raw.fill(0, 0, 16)
  }
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  if (
    !toneMatches(channel, decodeTone(raw.slice(10, 12))) ||
    (!toneMatches(channel, decodeTone(raw.slice(8, 10))) &&
      decodeTone(raw.slice(8, 10)).mode !== 'none')
  ) {
    const tone = encodeTone(channel)
    raw.set(tone, 8)
    raw.set(tone, 10)
  }
  raw[14] =
    (raw[14] & ~POWER_MASK) |
    ((codeFromPower(channel.power, profile) & 0x03) << 6)
  raw[15] =
    (raw[15] & ~(NARROW_MASK | SCAN_MASK)) |
    (channel.mode === 'NFM' || channel.mode === 'AM' ? NARROW_MASK : 0) |
    (channel.skip ? 0 : SCAN_MASK)
  const name = channel.name
    .replace(/[\x00-\x1f\x7f]/g, '')
    .slice(0, 12)
    .padEnd(12, '\u00ff')
  for (let index = 0; index < 12; index += 1) {
    raw[20 + index] = name.charCodeAt(index) & 0xff
  }
}

async function readAck(
  transport: RadioTransport,
  message: string,
): Promise<void> {
  if ((await transport.readExact(1, 3000))[0] !== ACK) throw new Error(message)
}

async function enterProgrammingMode(
  profile: JC8810Profile,
  transport: RadioTransport,
): Promise<void> {
  let lastError: unknown = null
  for (let attempt = 0; attempt < 5; attempt += 1) {
    try {
      await transport.discardInput()
      await transport.write(Uint8Array.from(profile.magic))
      await readAck(transport, 'The radio refused JC8810 programming mode.')
      await transport.write(Uint8Array.from([0x46]))
      const identity = await transport.readExact(8, 3000)
      if (
        !profile.fingerprints.some((fingerprint) =>
          fingerprint.every((value, index) => identity[index] === value),
        )
      ) {
        throw new Error(
          `The connected radio does not identify as ${profile.vendor} ${profile.model}.`,
        )
      }
      return
    } catch (cause) {
      lastError = cause
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error('No supported JC8810 radio responded.')
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(new TextEncoder().encode('E'))
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(
    Uint8Array.of(0x52, (address >> 8) & 0xff, address & 0xff, BLOCK_SIZE),
  )
  const response = await transport.readExact(4 + BLOCK_SIZE, 4000)
  if (
    response[0] !== 0x52 ||
    response[1] !== ((address >> 8) & 0xff) ||
    response[2] !== (address & 0xff) ||
    response[3] !== BLOCK_SIZE
  ) {
    throw new Error(`Unexpected JC8810 read reply at 0x${address.toString(16)}.`)
  }
  return response.slice(4)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(4 + data.byteLength)
  frame.set([
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
    data.byteLength,
  ])
  frame.set(data, 4)
  await transport.write(frame)
  await readAck(
    transport,
    `The radio rejected JC8810 write block 0x${address.toString(16)}.`,
  )
}

export async function readJC8810RadioWithTransport(
  profile: JC8810Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(profile, transport)
  try {
    const image = new Uint8Array(profile.memorySize)
    const total = profile.memorySize / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading JC8810-family memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await exitProgrammingMode(transport)
  }
}

export async function writeJC8810RadioWithTransport(
  profile: JC8810Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than this radio memory map.')
  }
  await enterProgrammingMode(profile, transport)
  try {
    const addresses = profile.ranges.flatMap(([start, end]) =>
      Array.from(
        { length: (end - start) / BLOCK_SIZE },
        (_, index) => start + index * BLOCK_SIZE,
      ),
    )
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing JC8810-family memory',
      })
    }
  } finally {
    await exitProgrammingMode(transport)
  }
}
